import  { useState, useEffect } from 'react';
import { ArrowLeft, Upload, FileText, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { EnrollmentApi } from '@/features/enrollment';

type Props = {
  handleNavigate: (targetIndex: number) => Promise<void>;
  handleSimpleNavigate?: (targetIndex: number) => void;
  handleNavigateWithCompletion?: (targetIndex: number, steps?: any[]) => Promise<void>;
  sessionId?: string | null;
  enrollmentData?: any;
  currentStepIndex?: number;
  updateEnrollmentData?: (key: string, value: any) => void;
};

export default function Step8Submit({ handleNavigate, handleSimpleNavigate, handleNavigateWithCompletion, sessionId, enrollmentData, currentStepIndex, updateEnrollmentData }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [intakeFormStatus, setIntakeFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [intakeFormMessage, setIntakeFormMessage] = useState('');
  const [intakeFormAlias, setIntakeFormAlias] = useState('');

  
  // Check form submission status from enrollmentData (root level)
useEffect(() => {
  
  // Read step8 data from root level, not nested under step8
  if (enrollmentData?.isFormSubmitted) {
    setSubmitted(enrollmentData?.isFormSuccess || false);
    if (enrollmentData?.isFormSuccess) {
      setIntakeFormStatus('success');
      setIntakeFormMessage('Form submitted successfully!');
    } else {
      setIntakeFormStatus('error');
      setIntakeFormMessage('Form submission failed. Please try again.');
    }
  }
}, [enrollmentData]);

  const handleBack = () => {
    // Calculate previous step dynamically
    const prevStepIndex = (currentStepIndex ?? 7) - 1
    
    if (handleSimpleNavigate) {
      handleSimpleNavigate(prevStepIndex)
    } else if (handleNavigate) {
      handleNavigate(prevStepIndex)
    }
  }

  const handleSubmitToFormstack = async () => {
    // Validate inputs
    if (!sessionId) {
      alert('Session ID is missing. Please start the process again.');
      return;
    }
    if (!enrollmentData || Object.keys(enrollmentData).length === 0) {
      alert('No enrollment data provided. Please complete the previous steps.');
      return;
    }

    try {
      setSubmitting(true);
      setIntakeFormStatus('submitting');
      setIntakeFormMessage('Submitting to intake form...');

      const campaign = enrollmentData.campaign
      const isRobloxCampaign = campaign.name?.toLowerCase().includes('roblox')
     
      let result;
      if (isRobloxCampaign) {
        // Use the new Roblox form submission endpoint
        result = await EnrollmentApi.submitRobloxForm({
          sessionId,
          ...enrollmentData
        });
      } else {
        // Use the existing generic submission
        result = await EnrollmentApi.submit(sessionId);
      }

      // Get intake form alias from campaign data using name pattern matching
      const intakeFormAlias = enrollmentData?.campaignName?.toLowerCase().includes('video game addiction') ? 'Video Game Addiction Intake Form' : 
                             enrollmentData?.campaignName?.toLowerCase().includes('roblox') ? 'Roblox Child Predator Intake Form' : 
                             enrollmentData?.campaignName?.toLowerCase().includes('video game addiction') ? 'Video Game Addiction Intake Form' :
                             enrollmentData?.campaignName?.toLowerCase().includes('roblox') ? 'Roblox Child Predator Intake Form' :
                             'Intake Form';

      // Check if Roblox form submission was successful
      if (isRobloxCampaign) {
        const robloxSubmissionSuccess = result?.data?.robloxSubmission?.success;
        if (robloxSubmissionSuccess) {
          setIntakeFormStatus('success');
          setIntakeFormMessage(`Successfully submitted to intake form: ${intakeFormAlias}`);
          
          // Update step8 data with Roblox form submission status
          if (updateEnrollmentData) {
            updateEnrollmentData('step8.isRobloxFormSubmitted', true);
            updateEnrollmentData('step8.isRobloxFormSuccess', true);
          }
        } else {
          setIntakeFormStatus('error');
          setIntakeFormMessage(`Roblox form submission failed: ${result?.data?.robloxSubmission?.message || 'Unknown error'}`);
          
          // Update step8 data to reflect failed submission
          if (updateEnrollmentData) {
            updateEnrollmentData('step8.isRobloxFormSubmitted', true);
            updateEnrollmentData('step8.isRobloxFormSuccess', false);
          }
        }
      } else {
        // For non-Roblox campaigns, assume success
        setIntakeFormStatus('success');
        setIntakeFormMessage(`Successfully submitted to intake form: ${intakeFormAlias}`);
      }
      setIntakeFormAlias(intakeFormAlias);

      // Store the intake form status in enrollment data
      if (updateEnrollmentData) {
        updateEnrollmentData('intakeFormSubmitted', true);
        updateEnrollmentData('intakeFormAlias', intakeFormAlias);
      }

      // Trigger status refresh for pending applications
      window.dispatchEvent(new CustomEvent('pending:updated'));

      // Set submitted state to show Continue button
      setSubmitted(true);
    } catch (error: any) {
      console.error('Error submitting data:', error);
      
      // Show more specific error messages
      let errorMessage = 'Failed to submit data. Please try again.';
      
      if (error?.response?.status === 400) {
        if (error?.response?.data?.error?.includes('email')) {
          errorMessage = 'Client email is required to send the retainer agreement. Please complete the intake contact step.';
        } else {
          errorMessage = error?.response?.data?.error || 'Invalid data provided. Please check all required fields.';
        }
      } else if (error?.response?.status === 404) {
        errorMessage = 'Session not found. Please start the enrollment process again.';
      } else if (error?.response?.status === 403) {
        errorMessage = 'You are not authorized to submit this session.';
      } else if (error?.response?.status === 401) {
        errorMessage = 'Please log in again to submit the data.';
      }
      
      setIntakeFormStatus('error');
      setIntakeFormMessage(`Failed to submit to intake form: ${errorMessage}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleContinue = async () => {
    try {
      setIsNavigating(true);
      const nextStepIndex = (currentStepIndex ?? 7) + 1 // ✅ Dynamic calculation
      
      
      if (handleNavigateWithCompletion) {
        await handleNavigateWithCompletion(nextStepIndex) // ✅ This handles everything
      } else {
        await handleNavigate(nextStepIndex)
      }
    } catch (error) {
      console.error('Error navigating to next step:', error);
      // If navigation fails (e.g., no next step), just stay on current page
    } finally {
      setIsNavigating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
            <Upload className="w-6 h-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="text-lg lg:text-xl font-semibold text-accent-900">Submit Client Data</h3>
            <p className="text-sm text-accent-600">Send retainer agreement to client for signature</p>
          </div>
        </div>

        {submitted ? (
          intakeFormStatus === 'success' ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 lg:p-6 mb-6">
              <div className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-green-900 mb-2">Form Submitted Successfully!</h4>
                  <p className="text-sm text-green-800 mb-4">
                    The retainer agreement has been sent to your client. You can now proceed to the next step.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 lg:p-6 mb-6">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-red-500 rounded-full mt-1 flex-shrink-0 flex items-center justify-center">
                  <span className="text-white text-xs font-bold">!</span>
                </div>
                <div>
                  <h4 className="font-semibold text-red-900 mb-2">Form Submission Failed</h4>
                  <p className="text-sm text-red-800 mb-4">
                    The form submission encountered an error. Please try again.
                  </p>
                </div>
              </div>
            </div>
          )
        ) : (
          // Not submitted yet - show ready to submit message
          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 lg:p-6 mb-6">
            <div className="flex items-start space-x-3">
              <FileText className="w-6 h-6 text-secondary-600 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-secondary-900 mb-2">Ready to Submit</h4>
                <p className="text-sm text-secondary-800 mb-4">
                  Click the Submit Data button to send the retainer agreement to your client for signature.
                </p>
                <div className="text-xs text-secondary-700 space-y-1">
                  <p>• All collected data will be sent to the proper intake form</p>
                  <p>• Client will receive retainer agreement via email</p>
                  <p>• Do not proceed until after clicking Submit Data</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show additional status messages if needed */}
        {intakeFormStatus === 'success' && (
          <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">
                ✅ Intake Form Submission: SUCCESS
              </span>
            </div>
            <p className="text-xs text-green-700 mt-1">
              Successfully submitted to: <strong>{intakeFormAlias}</strong>
            </p>
          </div>
        )}

        {intakeFormStatus === 'error' && (
          <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="text-sm font-medium text-red-800">
                ❌ Intake Form Submission: FAILED
              </span>
            </div>
            <p className="text-xs text-red-700 mt-1">
              {intakeFormMessage}
            </p>
          </div>
        )}

        {intakeFormStatus === 'submitting' && (
          <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-3 mb-4">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
              <span className="text-sm font-medium text-yellow-800">
                ⏳ Intake Form Submission: IN PROGRESS
              </span>
            </div>
            <p className="text-xs text-yellow-700 mt-1">
              {intakeFormMessage}
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-center gap-3">
          {submitted && intakeFormStatus === 'success' ? (
            <button
              className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 lg:py-4 px-8 lg:px-12 rounded-lg text-base lg:text-lg flex items-center space-x-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleContinue}
              disabled={isNavigating}
            >
              {isNavigating ? (
                <>
                  <Loader2 className="w-4 lg:w-5 h-4 lg:h-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 lg:w-5 h-4 lg:h-5" />
                  <span>Continue</span>
                </>
              )}
            </button>
          ) : (
            <button
              className={`${intakeFormStatus === 'error' ? 'bg-red-600 hover:bg-red-700' : 'bg-secondary-600 hover:bg-secondary-700'} text-white font-bold py-3 lg:py-4 px-8 lg:px-12 rounded-lg text-base lg:text-lg flex items-center space-x-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              onClick={handleSubmitToFormstack}
              disabled={submitting}
            >
              <Upload className="w-4 lg:w-5 h-4 lg:h-5" />
              <span>
                {submitting ? 'Submitting...' : 
                 intakeFormStatus === 'error' ? 'Retry Submission' : 'Submit Data'}
              </span>
            </button>
          )}
        </div>
      </div>

      {!submitted && (
        <div className="flex justify-between">
          <button
            className="btn-outline flex items-center space-x-2 min-w-[100px] justify-center"
            onClick={handleBack}
            disabled={submitting}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
        </div>
      )}
    </div>
  );
}